import { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import type { ConnectionDoc, UserDoc } from '@astrally/shared';
import { useApp } from '../../state/AppState';
import { useI18n } from '../../i18n';
import { ScreenHeader, Section, initialsOf } from '../../components/common';

const errText = (e: unknown) =>
  String(e)
    .replace(/^(FirebaseError|Error):\s*/g, '')
    .slice(0, 160);

export function MessagesScreen() {
  const { t } = useI18n();
  const app = useApp();
  const [names, setNames] = useState<Record<string, UserDoc | null>>({});
  const [outgoing, setOutgoing] = useState<ConnectionDoc[]>([]);
  const [loading, setLoading] = useState(true);
  const [notice, setNotice] = useState<{ tone: 'info' | 'danger'; text: string } | null>(null);
  const [confirmWithdraw, setConfirmWithdraw] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    let alive = true;
    Promise.all([
      app.refreshSocial().catch(() => undefined),
      app.backend
        .listConnections()
        .then((c) => {
          if (alive) setOutgoing(c.outgoing.filter((o) => o.status === 'pending'));
        })
        .catch(() => undefined),
    ]).finally(() => {
      if (alive) setLoading(false);
    });
    return () => {
      alive = false;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    const uids = new Set<string>();
    for (const c of app.conversations) for (const m of c.members) if (m !== app.uid) uids.add(m);
    for (const i of app.incomingConnections) uids.add(i.requesterUid);
    for (const o of outgoing) uids.add(o.recipientUid);
    for (const uid of uids) {
      if (!(uid in names)) {
        app.backend
          .getUser(uid)
          .then((u) => setNames((prev) => ({ ...prev, [uid]: u })))
          .catch(() => undefined);
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [app.conversations, app.incomingConnections, outgoing]);

  // A missing lookup falls back to a placeholder, never to the uid: a raw
  // Firebase id shown where a person's name belongs reads as leaked internals.
  const nameOf = (uid: string) => names[uid]?.displayName ?? '…';

  const respond = async (conn: ConnectionDoc, accept: boolean) => {
    setNotice(null);
    try {
      await app.respondConnection(conn.id, accept);
      setNotice({ tone: 'info', text: accept ? t('intro.accepted') : t('intro.declined') });
    } catch (e) {
      setNotice({ tone: 'danger', text: errText(e) });
    }
  };

  const withdraw = async (conn: ConnectionDoc) => {
    setBusy(true);
    setNotice(null);
    try {
      await app.backend.connectionWithdraw(conn.id);
      // Only now is the request actually gone. Removing the row first meant a
      // rejected call still looked like a withdrawal, while the request stayed
      // live on the other person's screen.
      setOutgoing((prev) => prev.filter((o) => o.id !== conn.id));
      setConfirmWithdraw(null);
    } catch (e) {
      setNotice({ tone: 'danger', text: errText(e) });
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="screen">
      <ScreenHeader title={t('msg.title')} />

      {notice && (
        <div
          className={`banner banner--${notice.tone === 'danger' ? 'danger' : 'info'} small spread`}
          style={{ marginBottom: 12 }}
        >
          <span>{notice.text}</span>
          <button className="chip" aria-label={t('common.close')} onClick={() => setNotice(null)}>✕</button>
        </div>
      )}

      {app.incomingConnections.length > 0 && (
        <Section title={t('req.incoming')}>
          <div className="col">
            {app.incomingConnections.map((conn) => (
              <div key={conn.id} className="card card--strong fade-in">
                <div className="row" style={{ marginBottom: 10 }}>
                  <div className="avatar avatar--gold" aria-hidden>{initialsOf(nameOf(conn.requesterUid))}</div>
                  <div style={{ flex: 1 }}>
                    <div className="h3">{nameOf(conn.requesterUid)}</div>
                    <div className="tiny muted">
                      would like to connect
                      {conn.introMessage ? ` — “${conn.introMessage}”` : ''}
                    </div>
                  </div>
                  <Link to={`/discover/${conn.requesterUid}`} className="tiny accent">
                    {t('discover.view')}
                  </Link>
                </div>
                <div className="row">
                  <button
                    className="btn btn--primary btn--small"
                    style={{ flex: 1 }}
                    onClick={() => respond(conn, true)}
                  >
                    {t('intro.accept')}
                  </button>
                  <button
                    className="btn btn--ghost btn--small"
                    style={{ flex: 1 }}
                    onClick={() => respond(conn, false)}
                  >
                    {t('intro.decline')}
                  </button>
                </div>
              </div>
            ))}
          </div>
        </Section>
      )}

      {outgoing.length > 0 && (
        <Section title={t('req.outgoing')}>
          <div className="col" style={{ gap: 0 }}>
            {outgoing.map((conn) => (
              <div key={conn.id} className="list-item" style={{ cursor: 'default', flexWrap: 'wrap' }}>
                <div className="avatar" aria-hidden>{initialsOf(nameOf(conn.recipientUid))}</div>
                <div style={{ flex: 1, minWidth: 0 }}>
                  <div className="h3">{nameOf(conn.recipientUid)}</div>
                  <div className="tiny muted">{t('req.pendingState')}</div>
                </div>
                {confirmWithdraw === conn.id ? (
                  // In-app confirmation: confirm() opens a system dialog the app
                  // cannot theme or translate.
                  <div className="col" style={{ width: '100%', marginTop: 10 }}>
                    <div className="tiny muted">
                      Withdraw this request? The consumed request is not refunded.
                    </div>
                    <div className="row">
                      <button
                        className="btn btn--ghost btn--small"
                        style={{ flex: 1 }}
                        disabled={busy}
                        onClick={() => setConfirmWithdraw(null)}
                      >
                        {t('common.cancel')}
                      </button>
                      <button
                        className="btn btn--danger btn--small"
                        style={{ flex: 1 }}
                        disabled={busy}
                        onClick={() => withdraw(conn)}
                      >
                        {busy ? <span className="spinner" /> : t('req.withdraw')}
                      </button>
                    </div>
                  </div>
                ) : (
                  <button className="chip" onClick={() => setConfirmWithdraw(conn.id)}>{t('req.withdraw')}</button>
                )}
              </div>
            ))}
          </div>
        </Section>
      )}

      <Section title={t('msg.title')}>
        {loading && app.conversations.length === 0 ? (
          <div style={{ display: 'grid', placeItems: 'center', padding: '32px 0' }}>
            <div className="spinner" />
          </div>
        ) : app.conversations.length === 0 ? (
          <div className="card muted small" style={{ lineHeight: 1.6 }}>
            {t('msg.empty')}
          </div>
        ) : (
          <div className="col" style={{ gap: 0 }}>
            {app.conversations.map((c) => {
              const other = c.members.find((m) => m !== app.uid) ?? '';
              const unread = c.unread?.[app.uid ?? ''] ?? 0;
              return (
                <Link key={c.id} to={`/messages/${c.id}`} style={{ color: 'inherit' }}>
                  <div className="list-item">
                    <div className="avatar" aria-hidden>{initialsOf(nameOf(other))}</div>
                    <div style={{ flex: 1, minWidth: 0 }}>
                      <div className="spread">
                        <span className="h3">{nameOf(other)}</span>
                        {c.status !== 'active' && <span className="tiny danger">{c.status}</span>}
                      </div>
                      <div
                        className="tiny muted"
                        style={{ overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}
                      >
                        {c.lastMessagePreview ?? 'Say hello — you are now connected.'}
                      </div>
                    </div>
                    {unread > 0 && <span className="badge badge--B">{unread}</span>}
                  </div>
                </Link>
              );
            })}
          </div>
        )}
      </Section>
    </div>
  );
}
